import { css } from '@styled-system/css';
import { InputNumber } from 'antd';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';

import { ProductFilterValues } from '@/features/analysisPageSellsHist/types';

interface ProductFilterModalProps {
  isOpen: boolean;
  filters: ProductFilterValues;
  onClose: () => void;
  onFilterChange: (filters: ProductFilterValues) => void;
  buttonRef: HTMLDivElement | null;
}

export const ProductFilterModal: FC<ProductFilterModalProps> = ({
  isOpen,
  filters,
  onClose,
  onFilterChange,
  buttonRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        buttonRef &&
        !buttonRef.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, buttonRef, onClose]);

  if (!isOpen) return null;
  return (
    <div
      ref={modalRef}
      className={css({
        position: 'absolute',
        top: '-20px',
        left: 'calc(100% + 3px)',
        bg: 'base.segBg',
        border: '0.5px solid',
        borderColor: 'base.border',
        borderRadius: 'lg',
        py: '15px',
        w: '495px',
        zIndex: 1000,
      })}
    >
      {/* Title */}
      <div className={css({ mb: 'md', px: '12px' })}>
        <h3
          className={css({
            fontSize: '13px',
            fontWeight: '500',
            fontFamily: 'Manrope, sans-serif',
            color: 'text.primary',
            m: 0,
            mb: '2',
          })}
        >
          Ajouter des filtres
        </h3>
        <div className={css({ h: '1px', bg: 'base.border', w: 'full' })} />
      </div>

      <div className={css({ display: 'flex', flexDirection: 'column', gap: 'md', px: '20px' })}>
        {/* Prix de vente */}
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '15px' })}>
          <label
            className={css({
              fontSize: '12px',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: '500',
              color: 'text.secondary',
            })}
          >
            Prix de vente
          </label>
          <div className={css({ display: 'flex', gap: 'lg', alignItems: 'center' })}>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.priceMin}
                onChange={(value) => onFilterChange({ ...filters, priceMin: value ?? undefined })}
                placeholder=""
                formatter={(value) => (value ? `${value} €` : '')}
                parser={(value) => value?.replace(' €', '') as any}
                min={0}
                max={filters.priceMax}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                min.
              </span>
            </div>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.priceMax}
                onChange={(value) => onFilterChange({ ...filters, priceMax: value ?? undefined })}
                placeholder=""
                formatter={(value) => (value ? `${value} €` : '')}
                parser={(value) => value?.replace(' €', '') as any}
                min={filters.priceMin ?? 0}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                max.
              </span>
            </div>
          </div>
        </div>

        {/* Nombre de favoris */}
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '15px' })}>
          <label
            className={css({
              fontSize: '12px',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: '500',
              color: 'text.secondary',
            })}
          >
            Nombre de favoris
          </label>
          <div className={css({ display: 'flex', gap: 'lg', alignItems: 'center' })}>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.favoritesMin}
                onChange={(value) => onFilterChange({ ...filters, favoritesMin: value ?? undefined })}
                placeholder=""
                min={0}
                max={filters.favoritesMax}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                min.
              </span>
            </div>
            <div className={css({ display: 'flex', gap: '2', alignItems: 'center', w: '210px' })}>
              <InputNumber
                value={filters.favoritesMax}
                onChange={(value) => onFilterChange({ ...filters, favoritesMax: value ?? undefined })}
                placeholder=""
                min={filters.favoritesMin ?? 0}
                style={{ width: '180px' }}
              />
              <span
                className={css({ fontSize: '11px', fontWeight: '500', color: 'text.lowkey', whiteSpace: 'nowrap' })}
              >
                max.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
